import { GROUND } from '../sim/constants';
import { TAU, ell, fillStroke, star5 } from './common';
import { ropeStroke } from './rope';

export interface FrogLook {
  x: number;
  y: number; // height above the pad
  air: boolean;
  stretch: number; // 0..1 while rising
  squash: number; // 0..1 after landing
  croakT: number; // seconds since the last croak
  tangleT: number; // seconds since tangled, -1 when free
  bubble: boolean;
  lookDY: number; // pupils follow the rope (-1 up .. 1 down)
  rope: string;
  ropeBack: string;
  hc: boolean;
  reduced: boolean;
  cold: boolean;
}

const G = '#68C14A';
const D = '#2F6E2B';
const LIGHT = '#9BDB6A';
const BELLY = '#E6F5B8';

export function drawFrogShadow(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  const k = Math.max(0.35, 1 - y / 140);
  ell(ctx, x, GROUND + 3, 44 * k, 7 * k);
  ctx.fillStyle = 'rgba(18,40,28,0.3)';
  ctx.fill();
}

function drawLoops(ctx: CanvasRenderingContext2D, f: FrogLook, front: boolean): void {
  for (let i = 0; i < 3; i++) {
    const p = Math.min(1, Math.max(0, f.tangleT * 4 - i * 0.7));
    if (p <= 0) continue;
    const y = -16 - i * 17;
    const rx = 44 - i * 4;
    ctx.beginPath();
    if (front) ctx.ellipse(0, y, rx, 8, -0.12, 0, Math.PI * p);
    else ctx.ellipse(0, y, rx, 8, -0.12, Math.PI, Math.PI + Math.PI * p);
    ropeStroke(ctx, f.rope, f.ropeBack, front ? 0.8 : -0.6, f.hc);
  }
}

export function drawFrog(ctx: CanvasRenderingContext2D, f: FrogLook): void {
  const tangled = f.tangleT >= 0;
  let sx = 1;
  let sy = 1;
  if (f.air) {
    sx = 1 - 0.08 * f.stretch;
    sy = 1 + 0.1 * f.stretch;
  } else if (f.squash > 0) {
    sx = 1 + 0.18 * f.squash;
    sy = 1 - 0.16 * f.squash;
  }
  ctx.save();
  ctx.translate(f.x, GROUND - f.y);
  if (tangled) {
    const wobble = f.reduced ? 0 : 0.25 * Math.sin(f.tangleT * 12) * Math.exp(-f.tangleT * 2.4);
    ctx.rotate(0.2 + wobble);
  }
  ctx.scale(sx, sy);

  if (tangled) drawLoops(ctx, f, false);

  for (const side of [-1, 1]) {
    if (f.air) {
      ell(ctx, side * 24, -14, 13, 19, side * -0.3);
      fillStroke(ctx, G, D, 3);
      ell(ctx, side * 31, 4, 6, 14, side * 0.25);
      fillStroke(ctx, G, D, 3);
      ell(ctx, side * 35, 18, 12, 4.5, side * 0.3);
      fillStroke(ctx, LIGHT, D, 2.5);
    } else {
      ell(ctx, side * 30, -16, 20, 15, side * 0.35);
      fillStroke(ctx, G, D, 3);
      ell(ctx, side * 44, -3, 16, 5.5);
      fillStroke(ctx, LIGHT, D, 2.5);
      for (let t = 0; t < 3; t++) {
        ell(ctx, side * (54 + t * 1.5), -6 + t * 3.5, 3, 3);
        fillStroke(ctx, LIGHT, D, 1.5);
      }
    }
  }
  // body and belly
  ell(ctx, 0, -34, 37, 30);
  fillStroke(ctx, G, D, 3);
  ell(ctx, 0, -25, 23, 18);
  ctx.fillStyle = BELLY;
  ctx.fill();
  ctx.fillStyle = 'rgba(47,110,43,0.45)';
  ell(ctx, -22, -46, 4.5, 3.5);
  ctx.fill();
  ell(ctx, 24, -38, 3.5, 3);
  ctx.fill();
  ell(ctx, -27, -30, 3, 2.5);
  ctx.fill();

  // throat sac swells while croaking
  if (f.croakT < 0.32 && !tangled) {
    const p = Math.sin((f.croakT / 0.32) * Math.PI);
    ell(ctx, 0, -33, 15 * p + 1, 11 * p + 1);
    fillStroke(ctx, 'rgba(250,245,200,0.95)', D, 2);
  }
  // arms
  for (const side of [-1, 1]) {
    ell(ctx, side * 16, -7, 7, 10, side * -0.2);
    fillStroke(ctx, G, D, 2.5);
    ell(ctx, side * 18, 1, 7, 3);
    fillStroke(ctx, LIGHT, D, 2);
  }
  // eyes
  const look = Math.max(-1, Math.min(1, f.lookDY)) * 3.5;
  for (const side of [-1, 1]) {
    const ex = side * 17;
    const ey = -60;
    ell(ctx, ex, ey, 15, 15);
    fillStroke(ctx, G, D, 3);
    ell(ctx, ex, ey, 10.5, 10.5);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();
    if (tangled) {
      ctx.strokeStyle = '#1B2B22';
      ctx.lineWidth = 2;
      ctx.beginPath();
      const spin = f.reduced ? 0 : f.tangleT * 6 * side;
      for (let a = 0; a < 11; a += 0.3) {
        const rr = a * 0.75;
        const px = ex + Math.cos(a + spin) * rr;
        const py = ey + Math.sin(a + spin) * rr;
        if (a === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.stroke();
    } else {
      ell(ctx, ex, ey + look, 5.5, 5.5);
      ctx.fillStyle = '#1B2B22';
      ctx.fill();
      ell(ctx, ex - 2, ey + look - 2.5, 1.8, 1.8);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
    }
  }
  // cheeks: pinker when it is cold
  ctx.fillStyle = f.cold ? 'rgba(255,100,120,0.65)' : 'rgba(255,120,130,0.45)';
  ell(ctx, -25, -43, 5, 3);
  ctx.fill();
  ell(ctx, 25, -43, 5, 3);
  ctx.fill();
  // mouth
  ctx.strokeStyle = D;
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  if (tangled) {
    ctx.moveTo(-12, -42);
    for (let x = -12; x <= 12; x += 3) ctx.lineTo(x, -42 + Math.sin(x * 0.8) * 2.2);
  } else if (f.croakT < 0.32) {
    ctx.ellipse(0, -44, 6, 4, 0, 0, TAU);
  } else {
    ctx.arc(0, -55, 14, 0.2 * Math.PI, 0.8 * Math.PI);
  }
  ctx.stroke();

  if (tangled) {
    drawLoops(ctx, f, true);
    for (let i = 0; i < 3; i++) {
      const a = (f.reduced ? 0 : f.tangleT * 4) + i * (TAU / 3);
      star5(ctx, Math.cos(a) * 30, -88 + Math.sin(a) * 7, 6, '#FFD84D', '#9A6A00');
    }
  }

  if (f.bubble) {
    const wob = f.reduced ? 0 : Math.sin(f.croakT * 6) * 2;
    ell(ctx, 0, -40, 62 + wob, 60 - wob);
    ctx.fillStyle = 'rgba(190,235,255,0.18)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(220,245,255,0.85)';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ell(ctx, -26, -74, 10, 5, -0.6);
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.fill();
  }
  ctx.restore();
}
